/**
 * V2VoiceGuideModal — edit the versioned Kindra brand-voice config that
 * steers the email composer's AI. One-per-line textareas; save = new version.
 */
import React, { useEffect, useState } from 'react';
import { C } from './V2AdminOSPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'kindra-admin-token';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ''}`, 'Content-Type': 'application/json' });

const SURFACE = '#1A1150', LINE = '#2A2070', ICY = '#DCF0F5';
const inputS = { width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${LINE}`, background: C.navy, color: ICY, fontFamily: 'Inter', fontSize: 12, outline: 'none' };
const label = { fontSize: 11, color: C.muted, fontFamily: 'Inter', display: 'block', marginBottom: 10 };

export default function V2VoiceGuideModal({ onClose, showToast }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/admin/outreach/brand_voice`, { headers: auth() })
      .then((r) => r.json()).then((d) => setCfg(d.config))
      .catch(() => showToast('Voice guide load failed'));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/outreach/brand_voice`, { method: 'PUT', headers: auth(), body: JSON.stringify(cfg) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'HTTP ' + r.status);
      setCfg(d.config);
      showToast(`✓ Voice guide saved — now version ${d.config.version}`);
      onClose();
    } catch (e) { showToast('Save failed: ' + e.message); }
    finally { setBusy(false); }
  };

  const lines = (arr) => (arr || []).join('\n');
  const setLines = (key) => (e) => setCfg({ ...cfg, [key]: e.target.value.split('\n').filter((x) => x.trim()) });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(16,8,56,0.85)', backdropFilter: 'blur(4px)', zIndex: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} data-testid="voice-guide-modal">
      <div style={{ background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 16, padding: 24, maxWidth: 680, width: '100%', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <p style={{ fontFamily: 'Poppins,Inter,sans-serif', fontWeight: 700, fontSize: 18, color: ICY, margin: 0 }}>Brand voice guide</p>
          {cfg && <span style={{ fontSize: 11, color: C.faint, fontFamily: 'Inter' }}>version {cfg.version}</span>}
        </div>
        <p style={{ fontFamily: 'Inter', fontSize: 12, color: C.muted, margin: '0 0 16px' }}>
          The AI reads this on every draft. Saving creates a new version — old versions stay on file.
        </p>
        {!cfg ? <p style={{ color: C.faint, fontFamily: 'Inter' }}>Loading…</p> : (
          <>
            <label style={label}>We are / we are not (one per line)
              <textarea data-testid="voice-we-are" rows={7} value={lines(cfg.we_are)} onChange={setLines('we_are')} style={{ ...inputS, marginTop: 4 }} />
            </label>
            <label style={label}>The seven rules (one per line)
              <textarea data-testid="voice-rules" rows={7} value={lines(cfg.rules)} onChange={setLines('rules')} style={{ ...inputS, marginTop: 4 }} />
            </label>
            <label style={label}>Banned words (one per line — validator auto-fixes drafts that use them)
              <textarea data-testid="voice-banned" rows={6} value={lines(cfg.banned_words)} onChange={setLines('banned_words')} style={{ ...inputS, marginTop: 4 }} />
            </label>
            <label style={label}>The five client pains (one per line)
              <textarea data-testid="voice-pains" rows={5} value={lines(cfg.pains)} onChange={setLines('pains')} style={{ ...inputS, marginTop: 4 }} />
            </label>
            <p style={{ fontSize: 11, color: C.muted, fontFamily: 'Inter', margin: '0 0 6px' }}>Tone by context</p>
            {Object.entries(cfg.tone_by_context || {}).map(([k, v]) => (
              <label key={k} style={label}>{k}
                <input value={v} onChange={(e) => setCfg({ ...cfg, tone_by_context: { ...cfg.tone_by_context, [k]: e.target.value } })} style={{ ...inputS, marginTop: 4 }} />
              </label>
            ))}
            <p style={{ fontSize: 11, color: C.muted, fontFamily: 'Inter', margin: '0 0 6px' }}>Michael&apos;s settings</p>
            {Object.entries(cfg.settings || {}).map(([k, v]) => (
              <label key={k} style={label}>{k.replace(/_/g, ' ')}
                <input data-testid={`voice-setting-${k}`} value={v} onChange={(e) => setCfg({ ...cfg, settings: { ...cfg.settings, [k]: e.target.value } })} style={{ ...inputS, marginTop: 4 }} />
              </label>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button onClick={onClose} style={{ background: 'transparent', color: C.muted, border: `1px solid ${LINE}`, borderRadius: 999, padding: '8px 16px', fontFamily: 'Inter', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button data-testid="voice-save" onClick={save} disabled={busy}
                style={{ background: C.spectrum, color: '#fff', border: 'none', borderRadius: 999, padding: '8px 18px', fontFamily: 'Inter', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                {busy ? 'Saving…' : 'Save (new version)'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
